import { cva } from 'class-variance-authority';

/**
 * Wygląd przycisku bez samego `<button>` — dla miejsc, w których element musi
 * być odnośnikiem (pasek powrotu ekranu rekordu). Nowego przycisku z tego nie
 * budujemy: `ActionButton` zostaje jedynym.
 */
export const buttonVariants = cva(
  'relative inline-flex shrink-0 items-center justify-center gap-2 rounded-[9px] transition-colors disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      tone: {
        /** Akcja wtórna: „Wróć do edycji”, „Wstecz”, „Odrzucam zmiany”. */
        outline:
          'border border-neutral-200 bg-basic-white font-semibold text-neutral-800 hover:bg-neutral-50',
        /** Akcja wtórna modalu — ciemniejszy napis, hover na ramce. */
        neutral:
          'border border-neutral-200 bg-basic-white font-semibold text-ink hover:border-neutral-400',
        /** Akcja bez własnego tła — paski narzędzi, akcje wiersza. */
        ghost:
          'bg-transparent font-semibold text-neutral-800 hover:bg-neutral-100',
        /** Akcja wtórna w bursztynie — obok głównej, gdy obie są równorzędne. */
        secondary:
          'border border-accent-border bg-accent-100 font-semibold text-accent-900 hover:bg-accent-200',
        /** Główna akcja ciemna: „Dalej: rozliczenie →”, „Wprowadź paczkę”. */
        dark: 'border border-ink bg-ink font-bold text-basic-white hover:bg-neutral-900',
        /** Główna akcja bursztynowa: „Wprowadź paczkę”, „Wyślij do akceptacji →”. */
        accent:
          'border border-accent-600 bg-accent-600 font-bold text-ink hover:bg-accent-700',
        /** Zgoda klienta na ekranie akceptacji zmian. */
        success:
          'border border-success-300 bg-success-300 font-bold text-basic-white hover:bg-success-300/90',
        /** Akcja niszcząca z obwiedzią — modal potwierdzenia. */
        danger:
          'border border-error-100 bg-basic-white font-semibold text-error-300 hover:border-error-200',
        /** Akcja niszcząca bez ramki — panel boczny. */
        dangerGhost:
          'bg-transparent font-semibold text-error-300 hover:bg-error-50',
        /**
         * Dopisanie pozycji: „+ Wolna linia”, „+ Zniżka”. Kreskowana obwódka
         * odróżnia go od akcji, które coś rozstrzygają.
         */
        dashed:
          'border border-dashed border-neutral-300 bg-basic-white font-semibold text-neutral-800 hover:border-neutral-500',
      },
      size: {
        /** Akcja w wierszu tabeli i innych ciasnych miejscach. */
        xs: 'px-2 py-1 text-xs leading-[16px] [&_svg]:size-4',
        /**
         * Kontrolka paska narzędzi tabeli i akcja wiersza na liście ustawień —
         * 30 px wysokości, ta sama skala co `TableButtonMock` i `SearchAction`.
         */
        toolbar: 'h-[30px] px-2.5 py-1 text-xs leading-[16px] [&_svg]:size-3.5',
        /** Akcja poboczna panelu bocznego. */
        sm: 'px-3 py-[7px] text-sm leading-[20px] [&_svg]:size-4',
        md: 'px-[18px] py-[11px] text-[15px] [&_svg]:size-[18px]',
        lg: 'px-[22px] py-[13px] text-[15px] [&_svg]:size-5',
        /** Pełnoszerokościowy wybór w modalu. */
        block: 'w-full px-[18px] py-[13px] text-[15px] [&_svg]:size-5',
      },
      /** Sam znak bez napisu — kwadrat o wysokości odpowiadającego rozmiaru. */
      iconOnly: {
        true: 'p-0',
        false: '',
      },
    },
    compoundVariants: [
      { iconOnly: true, size: 'xs', class: 'size-[26px]' },
      { iconOnly: true, size: 'toolbar', class: 'size-[30px]' },
      { iconOnly: true, size: 'sm', class: 'size-[34px]' },
      { iconOnly: true, size: 'md', class: 'size-11' },
      { iconOnly: true, size: 'lg', class: 'size-12' },
      { iconOnly: true, size: 'block', class: 'size-12' },
    ],
    defaultVariants: {
      tone: 'outline',
      size: 'md',
      iconOnly: false,
    },
  },
);
